import PropTypes from 'prop-types';
import { DELIVERY_REFINEMENTS, FULFILLMENT_DIMENSIONS_LABELS } from './constants';

const {
  arrayOf,
  bool,
  obj,
  number,
  oneOf,
  oneOfType,
  shape,
  string
} = PropTypes;

export const refinementPropType = shape({
  url: string,
  refinementKey: string,
  label: string.isRequired,
  recordCount: string,
  selected: oneOfType([bool, null]),
  imgUrl: oneOfType([string, null]),
  nestedRefinements: oneOfType([arrayOf(obj), null]),
  __typename: oneOf(['Refinement'])
});

export const dimensionPropType = shape({
  label: string,
  refinements: arrayOf(refinementPropType),
  collapse: bool,
  dimensionId: string,
  isVisualNav: bool,
  isVisualDimension: oneOfType([bool, null]),
  isNumericFilter: oneOfType([bool, null]),
  isColorSwatch: oneOfType([bool, null]),
  nestedRefinementsLimit: number,
  visualNavSequence: string,
  __typename: oneOf(['Dimension'])
});

export const deliveryOptionPropType = shape({
  label: oneOf([DELIVERY_REFINEMENTS.ANYTIME, DELIVERY_REFINEMENTS.TODAY, DELIVERY_REFINEMENTS.BY_TOMORROW]).isRequired,
  isDisabled: bool,
  isHidden: bool,
  isSelected: bool
});

const toTitleCase = (str) => {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );
};

const getDeliveryDimension = (dimensions, dimensionLabel) => {
  return dimensions?.find(
    (dimension) => typeof dimension.label === 'string'
      && dimension.label.toLowerCase() === dimensionLabel.toLowerCase()
  );
};

/**
 * Finds and returns a refinement with the specified label from a given dimension.
 *
 * @param {Array<dimensionPropType>} dimensions - Array of dimension objects to search.
 * @param {string} dimensionName - Label of the dimension to search within.
 * @param {string} refinementName - Label of the refinement to find.
 * @returns {refinementPropType|null} The matching refinement object if found; otherwise, null.
 */
const getSingleDeliveryRefinement = (dimensions, dimensionName, refinementName) => {
  const targetDimension = getDeliveryDimension(dimensions, dimensionName);
  if (!targetDimension) {
    return null;
  }
  return targetDimension.refinements.find((refinement) => refinement.label === refinementName);
};

const isDeliveryRefinementEligible = (dimensions, dimensionName, refinementName) => {
  const selectedRefinement = getSingleDeliveryRefinement(dimensions, dimensionName, refinementName);
  return !!selectedRefinement?.recordCount && selectedRefinement.recordCount !== '0';
};

const isDeliveryRefinementEligibleInDimensions = (dimensions, dimensionNames, refinementName) => {
  return dimensionNames
    .some((dimensionName) => isDeliveryRefinementEligible(dimensions, dimensionName, refinementName));
};

/**
 * Checks if at least one of the specified refinements is eligible in any of the given dimensions.
 *
 * @param {Array<dimensionPropType>} dimensions - Dimensions to search.
 * @param {Array<string>} dimensionNames - Labels of dimensions to check.
 * @param {Array<string>} refinementNames - Refinement labels to check for eligibility.
 * @returns {boolean} True if any refinement is eligible in any dimension; otherwise, false.
 */
const areAnyRefinementsEligibleInDimensions = (dimensions, dimensionNames, refinementNames) => {
  return refinementNames.some((refinementName) => (
    isDeliveryRefinementEligibleInDimensions(dimensions, dimensionNames, refinementName)
  ));
};

/**
 * Checks if either "Today" or "By Tomorrow" delivery options are eligible
 * in any of the specified dimensions.
 *
 * @param {Array<dimensionPropType>} dimensions - Array of dimension objects to search.
 * @param {Array<string>} dimensionNames - Array of dimension labels to check for eligibility.
 * @returns {boolean} True if at least one of "Today" or "By Tomorrow" is eligible in any dimension.
 */
const isTodayOrByTomorrowEligibleInSomeDimensions = (dimensions, dimensionNames) => {
  return areAnyRefinementsEligibleInDimensions(
    dimensions, dimensionNames, [DELIVERY_REFINEMENTS.TODAY, DELIVERY_REFINEMENTS.BY_TOMORROW]
  );
};

/**
 * Generates data for the delivery speed options for the DeliveryOptions component
 * based on the provided dimensions and the currently selected delivery dimension.
 *
 * @param {Array<dimensionPropType>} dimensions - Array of dimension objects.
 * @param {string} selectedDeliveryDimension - The label of the currently selected delivery dimension.
 * @returns {Array<deliveryOptionPropType>} Array of delivery option props.
 */
const generateDeliveryOptions = (dimensions, selectedDeliveryDimension) => {
  const createOptionProps = (refinementName) => ({
    label: refinementName,
    isSelected: getSingleDeliveryRefinement(dimensions, selectedDeliveryDimension, refinementName)?.selected || false,
    isDisabled: !isDeliveryRefinementEligible(dimensions, selectedDeliveryDimension, refinementName),
    isHidden: !isDeliveryRefinementEligibleInDimensions(
      dimensions, [FULFILLMENT_DIMENSIONS_LABELS.DELIVERY, FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY], refinementName
    ),
    url: getSingleDeliveryRefinement(dimensions, selectedDeliveryDimension, refinementName)?.url || ''
  });

  return [
    createOptionProps(DELIVERY_REFINEMENTS.ANYTIME),
    createOptionProps(DELIVERY_REFINEMENTS.TODAY),
    createOptionProps(DELIVERY_REFINEMENTS.BY_TOMORROW)
  ];
};

export {
  areAnyRefinementsEligibleInDimensions,
  isTodayOrByTomorrowEligibleInSomeDimensions,
  getDeliveryDimension,
  generateDeliveryOptions,
  getSingleDeliveryRefinement,
  toTitleCase
};
